/**
 * The identity document itself, on screen.
 *
 * This is the most sensitive view in the console. A BVN slip or a passport page is the whole of
 * somebody's identity in one image, so three things are true of it and all three are visible on
 * the dialog rather than only in a comment:
 *
 *   - **It is fetched with a bearer token**, through the same binary path a support attachment
 *     uses. It can never be a URL that works without a session, which is also why it cannot be
 *     an `<img src>` and has to be an object URL built here.
 *   - **Opening it is audited.** The API writes a row naming the one document that was opened
 *     and who opened it. The dialog says so, because a reviewer who knows the read is recorded
 *     behaves differently from one who assumes it is not, and that difference is the control.
 *   - **The bytes are released when it closes.** `URL.revokeObjectURL` on unmount, so a document
 *     does not sit in memory behind whatever the operator does next.
 *
 * Images and PDFs both, because a reviewer meets both. A viewer only ever tested against an
 * image renders a broken frame the first time somebody uploads a scan from their bank, which is
 * exactly the moment it matters.
 */
import { useEffect, useRef, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { MediaObject } from '../api/client.js';
import type { KycDocumentSummary } from '../contracts/types.js';
import { Button, ErrorBox, LoadingState } from './ui.js';
import { Modal } from './Modal.js';
import { IconDownload } from './icons.js';
import { humanize } from '../lib/format.js';
import { formatBytes } from '../lib/emailAttachments.js';

export function DocumentViewer({
  document: doc,
  onClose,
}: {
  readonly document: KycDocumentSummary;
  readonly onClose: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const [media, setMedia] = useState<MediaObject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const held = useRef<MediaObject | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getKycDocument(doc.id)
      .then((result) => {
        if (!live) {
          result.revoke();
          return;
        }
        held.current = result;
        setMedia(result);
      })
      .catch((err: unknown) => {
        if (live) setError(messageOf(err));
      });
    return () => {
      live = false;
    };
  }, [api, doc.id]);

  // Released when the dialog closes, not merely when the page changes. A document left in an
  // object URL is a document still in this tab's memory.
  useEffect(() => {
    return () => {
      held.current?.revoke();
      held.current = null;
    };
  }, []);

  const isPdf = doc.mime === 'application/pdf';

  return (
    <Modal
      open
      size="wide"
      title={humanize(doc.kind)}
      description="Fetched with your operator session and recorded against your name. This view is audited."
      onClose={onClose}
      footer={
        <>
          {media !== null && (
            <a className="btn btn--ghost" href={media.url} download={media.name}>
              <IconDownload size={15} /> Download
            </a>
          )}
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="docview">
        {error !== null ? (
          <ErrorBox message={error} />
        ) : media === null ? (
          <LoadingState label="Loading the document…" />
        ) : isPdf ? (
          // An `<iframe>` rather than `<embed>`: it is the one the browser's own PDF viewer is
          // reliably wired to, and a blob URL in it inherits no origin of its own.
          <iframe className="docview__frame" src={media.url} title={humanize(doc.kind)} />
        ) : (
          <img className="docview__image" src={media.url} alt={`${humanize(doc.kind)} document`} />
        )}

        <dl className="kv docview__meta">
          <dt>Type</dt>
          <dd>{humanize(doc.kind)}</dd>
          <dt>Format</dt>
          <dd className="mono">{doc.mime}</dd>
          <dt>Size</dt>
          <dd>{formatBytes(doc.byteLength)}</dd>
          <dt>Uploaded</dt>
          <dd>{new Date(doc.uploadedAt).toLocaleString()}</dd>
          <dt>Checksum</dt>
          {/*
            The SHA-256 of the stored plaintext, shown in full rather than truncated. It is what
            lets a reviewer prove the bytes they were served are the bytes on file, which is the
            only reason it is worth showing at all, and half a digest proves nothing.
          */}
          <dd className="mono docview__sha">{doc.sha256}</dd>
        </dl>
      </div>
    </Modal>
  );
}
